<template>
  <gas-page-shell :breadcrumb="['告警管理', '实时告警']">
    <div class="stat-row">
      <div v-for="item in statCards" :key="item.key" class="stat-card">
        <div class="stat-icon" :style="{ background: item.bg, color: item.color }"><i :class="item.icon"></i></div>
        <div class="stat-body">
          <div class="stat-label">{{ item.label }}</div>
          <div class="stat-value" :style="{ color: item.valueColor || '#1d2129' }">
            {{ item.value }}<span v-if="item.sub" class="stat-sub">{{ item.sub }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="search-panel">
      <el-form :inline="true" :model="query" size="small" label-width="72px" class="search-form">
        <el-form-item label="告警时间">
          <el-date-picker
            v-model="timeRange"
            type="datetimerange"
            range-separator="至"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
            value-format="yyyy-MM-dd HH:mm:ss"
            :default-time="['00:00:00', '23:59:59']"
            style="width:360px"
            @change="onTimeRangeChange"
          />
        </el-form-item>
        <el-form-item label="告警级别">
          <el-select v-model="query.alarmLevel" clearable placeholder="全部级别" style="width:120px">
            <el-option v-for="l in levelOptions" :key="l.value" :label="l.label" :value="l.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="告警类型">
          <el-select v-model="query.alarmType" clearable placeholder="全部类型" style="width:140px">
            <el-option v-for="t in typeOptions" :key="t.value" :label="t.label" :value="t.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="站点名称">
          <el-select v-model="query.siteName" clearable filterable placeholder="请选择站点" style="width:160px">
            <el-option v-for="s in siteOptions" :key="s" :label="s" :value="s" />
          </el-select>
        </el-form-item>
        <el-form-item label="表具名称">
          <el-input v-model="query.meterName" clearable placeholder="请输入表具名称" style="width:160px" />
        </el-form-item>
        <el-form-item label="设备类型">
          <el-select v-model="query.deviceType" clearable placeholder="全部类型" style="width:160px">
            <el-option v-for="t in deviceTypeOptions" :key="t" :label="t" :value="t" />
          </el-select>
        </el-form-item>
        <el-form-item label="设备编号">
          <el-input v-model="query.deviceCode" clearable placeholder="设备/终端编号" style="width:160px" @keyup.enter.native="search" />
        </el-form-item>
        <el-form-item label="处理状态">
          <el-select v-model="query.alarmStatus" clearable placeholder="全部状态" style="width:120px">
            <el-option v-for="s in statusOptions" :key="s.value" :label="s.label" :value="s.value" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button @click="resetSearch">重置</el-button>
          <el-button type="primary" icon="el-icon-search" @click="search">查询</el-button>
        </el-form-item>
      </el-form>
    </div>

    <div class="table-panel">
      <div class="table-toolbar">
        <div class="level-tabs">
          <span
            v-for="tab in levelTabs"
            :key="tab.value"
            class="level-tab"
            :class="{ active: activeLevelTab === tab.value }"
            @click="switchLevelTab(tab.value)"
          >
            <i v-if="tab.dot" class="level-dot" :class="'dot-' + tab.dot"></i>
            {{ tab.label }}
          </span>
        </div>
      </div>
      <div class="table-head">
        <div class="table-title-group">
          <span class="table-title">告警列表 <em>共 {{ total }} 条记录</em></span>
          <gas-table-toolbar
            :columns="tableColumnDefs"
            :visible-map.sync="tableColumnVisible"
            :selected-count="selectedIds.length"
            :show-batch-delete="tableCanBatchDelete"
            @batch-delete="batchRemove"
          />
        </div>
        <el-button type="success" size="small" icon="el-icon-download" :loading="exporting" @click="exportExcel">导出Excel</el-button>
      </div>

      <el-table
        :data="list"
        v-loading="loading"
        border
        size="small"
        class="alarm-table"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="44" align="center" />
        <el-table-column v-if="colVisible('rowNo')" label="序号" width="64" align="center">
          <template slot-scope="{ $index }">{{ rowNo($index) }}</template>
        </el-table-column>
        <el-table-column v-if="colVisible('alarmTime')" prop="alarmTime" label="告警时间" min-width="150" />
        <el-table-column v-if="colVisible('siteName')" prop="siteName" label="站点名称" min-width="130" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.siteName || '—' }}</template>
        </el-table-column>
        <el-table-column v-if="colVisible('meterName')" prop="meterName" label="表具名称" min-width="120" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.meterName || row.terminalCode || '—' }}</template>
        </el-table-column>
        <el-table-column v-if="colVisible('deviceType')" prop="deviceType" label="设备类型" width="110" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.deviceType || '—' }}</template>
        </el-table-column>
        <el-table-column v-if="colVisible('deviceCode')" label="设备编号" min-width="120" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.deviceCode || row.terminalCode || '—' }}</template>
        </el-table-column>
        <el-table-column v-if="colVisible('alarmType')" label="报警类型" width="108" align="center">
          <template slot-scope="{ row }">
            <span class="type-tag" :class="typeTagClass(row.alarmType)">{{ alarmTypeLabel(row.alarmType) }}</span>
          </template>
        </el-table-column>
        <el-table-column v-if="colVisible('timeoutSec')" label="超时时间(秒)" width="110" align="center">
          <template slot-scope="{ row }">
            <span :class="{ 'timeout-warn': row.timeoutSeconds > 300 }">{{ formatTimeout(row.timeoutSeconds) }}</span>
          </template>
        </el-table-column>
        <el-table-column v-if="colVisible('alarmValue')" label="报警值" min-width="110" align="center">
          <template slot-scope="{ row }">
            <span :class="valueClass(row)">{{ row.alarmValue || '—' }}</span>
          </template>
        </el-table-column>
        <el-table-column v-if="colVisible('setValue')" label="设置值" min-width="110" align="center">
          <template slot-scope="{ row }">{{ row.setValue || '—' }}</template>
        </el-table-column>
        <el-table-column v-if="colVisible('currentValue')" label="当前值" min-width="110" align="center">
          <template slot-scope="{ row }">
            <span :class="currentValueClass(row)">{{ row.currentValue || '—' }}</span>
          </template>
        </el-table-column>
        <el-table-column v-if="colVisible('operator')" label="操作人" width="88" align="center">
          <template slot-scope="{ row }">{{ row.operatorName || '—' }}</template>
        </el-table-column>
        <el-table-column v-if="colVisible('status')" label="状态" width="88" align="center">
          <template slot-scope="{ row }">
            <span class="status-text" :class="'st-' + normalizeStatus(row.alarmStatus)">
              {{ statusLabel(row.alarmStatus) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="168" fixed="right" align="center">
          <template slot-scope="{ row }">
            <el-button
              v-if="canProcess(row)"
              type="text"
              size="small"
              class="op-process"
              @click="processAlarm(row)"
            >处理</el-button>
            <el-button
              v-if="canFinish(row)"
              type="text"
              size="small"
              class="op-finish"
              @click="finishAlarm(row)"
            >完成</el-button>
            <el-button type="text" size="small" @click="openDetail(row)">详情</el-button>
            <el-button
              v-if="canShield(row)"
              type="text"
              size="small"
              class="op-shield"
              @click="shieldAlarm(row)"
            >屏蔽</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="pager"
        background
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        :current-page="query.pageNum"
        :page-size="query.pageSize"
        :page-sizes="[10, 20, 50]"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <el-dialog title="告警详情" :visible.sync="detailVisible" width="620px">
      <el-descriptions v-if="detail" :column="2" border size="small">
        <el-descriptions-item label="告警时间" :span="2">{{ detail.alarmTime || '—' }}</el-descriptions-item>
        <el-descriptions-item label="站点名称">{{ detail.siteName || '—' }}</el-descriptions-item>
        <el-descriptions-item label="表具名称">{{ detail.meterName || '—' }}</el-descriptions-item>
        <el-descriptions-item label="报警类型">{{ alarmTypeLabel(detail.alarmType) }}</el-descriptions-item>
        <el-descriptions-item label="告警级别">{{ levelLabel(detail.alarmLevel) }}</el-descriptions-item>
        <el-descriptions-item label="报警值">{{ detail.alarmValue || '—' }}</el-descriptions-item>
        <el-descriptions-item label="设置值">{{ detail.setValue || '—' }}</el-descriptions-item>
        <el-descriptions-item label="当前值">{{ detail.currentValue || '—' }}</el-descriptions-item>
        <el-descriptions-item label="超时(秒)">{{ formatTimeout(detail.timeoutSeconds) }}</el-descriptions-item>
        <el-descriptions-item label="状态">{{ statusLabel(detail.alarmStatus) }}</el-descriptions-item>
        <el-descriptions-item label="操作人">{{ detail.operatorName || '—' }}</el-descriptions-item>
        <el-descriptions-item label="告警标题" :span="2">{{ detail.title || '—' }}</el-descriptions-item>
        <el-descriptions-item label="告警内容" :span="2">{{ detail.content || '—' }}</el-descriptions-item>
      </el-descriptions>
    </el-dialog>
  </gas-page-shell>
</template>

<script>
import GasPageShell from '@/components/GasPageShell.vue'
import gasCrudPage from '@/mixins/gasCrudPage'
import deviceTypeOptionsMixin from '@/mixins/deviceTypeOptions'
import { ALARM_REALTIME_COLUMNS } from '@/config/tableColumnPresets'
import { gasApi } from '@/api/gas'
import { resolveStatus } from '@/utils/statusTag'

const TYPE_MAP = {
  data_overlimit: { label: '数据超限', tag: 'type-danger' },
  device_offline: { label: '设备离线', tag: 'type-danger' },
  low_voltage: { label: '低电压告警', tag: 'type-warn' },
  data_fluctuation: { label: '数据波动', tag: 'type-info' }
}

export default {
  components: { GasPageShell },
  mixins: [gasCrudPage, deviceTypeOptionsMixin],
  data() {
    return {
      autoLoad: false,
      idField: 'alarmId',
      listApi: gasApi.alarm.list,
      saveApi: null,
      removeApi: gasApi.alarm.remove,
      batchRemoveApi: gasApi.alarm.batchRemove,
      statCards: [],
      siteOptions: [],
      timeRange: null,
      detailVisible: false,
      detail: null,
      exporting: false,
      alarmRefreshTimer: null,
      activeLevelTab: '',
      levelOptions: [
        { label: '紧急', value: 'critical' },
        { label: '重要', value: 'warning' },
        { label: '一般', value: 'normal' },
        { label: '提示', value: 'info' }
      ],
      typeOptions: [
        { label: '数据超限', value: 'data_overlimit' },
        { label: '设备离线', value: 'device_offline' },
        { label: '低电压告警', value: 'low_voltage' },
        { label: '数据波动', value: 'data_fluctuation' }
      ],
      statusOptions: [
        { label: '待处理', value: 'pending' },
        { label: '处理中', value: 'processing' },
        { label: '已处理', value: 'resolved' }
      ],
      levelTabs: [
        { label: '全部', value: '' },
        { label: '紧急', value: 'critical', dot: 'critical' },
        { label: '重要', value: 'warning', dot: 'warning' },
        { label: '一般', value: 'normal', dot: 'normal' },
        { label: '提示', value: 'info', dot: 'info' }
      ],
      query: {
        pageNum: 1,
        pageSize: 10,
        alarmStatus: '',
        alarmLevel: '',
        alarmType: '',
        siteName: '',
        meterName: '',
        deviceType: '',
        deviceCode: '',
        beginDate: '',
        endDate: '',
        realtimeOnly: '1'
      }
    }
  },
  created() {
    this.initTableColumns(ALARM_REALTIME_COLUMNS)
    this.loadDeviceTypeOptions()
    this.loadStats()
    this.loadSiteOptions()
    this.loadData()
    this.alarmRefreshTimer = setInterval(this.refreshAll, 5000)
  },
  beforeDestroy() {
    if (this.alarmRefreshTimer) clearInterval(this.alarmRefreshTimer)
  },
  methods: {
    buildQuery() {
      const p = { ...this.query }
      Object.keys(p).forEach(k => {
        if (p[k] === '') p[k] = undefined
      })
      return p
    },
    async loadData() {
      if (!this.listApi) return
      this.loading = true
      try {
        const res = await this.listApi(this.buildQuery())
        const data = res.data || {}
        this.list = data.records || []
        this.total = Number(data.total || 0)
      } finally {
        this.loading = false
      }
    },
    async loadStats() {
      try {
        const res = await gasApi.alarm.realtimeStats()
        const d = res.data || {}
        this.statCards = [
          { key: 'pending', label: '待处理告警', value: d.pendingCount || 0, icon: 'el-icon-bell', color: '#f53f3f', bg: '#ffece8', valueColor: '#f53f3f' },
          { key: 'today', label: '今日告警总数', value: d.todayTotal || 0, icon: 'el-icon-warning-outline', color: '#ff7d00', bg: '#fff7e8', valueColor: '#ff7d00' },
          { key: 'resolved', label: '今日已处理', value: d.todayResolved || 0, icon: 'el-icon-circle-check', color: '#00b42a', bg: '#e8ffea', valueColor: '#00b42a' },
          {
            key: 'sites',
            label: '告警站点数',
            value: d.alarmSites || 0,
            sub: d.totalSites ? ` / 共 ${d.totalSites} 个站点` : '',
            icon: 'el-icon-location-outline',
            color: '#1677ff',
            bg: '#e8f3ff',
            valueColor: '#1677ff'
          }
        ]
      } catch (e) {
        this.statCards = []
      }
    },
    async loadSiteOptions() {
      try {
        const res = await gasApi.alarm.siteOptions()
        this.siteOptions = res.data || []
      } catch (e) {
        this.siteOptions = []
      }
    },
    refreshAll() {
      this.loadStats()
      this.loadData()
    },
    search() {
      this.query.pageNum = 1
      this.refreshAll()
    },
    resetSearch() {
      this.activeLevelTab = ''
      this.timeRange = null
      this.query = {
        pageNum: 1,
        pageSize: this.query.pageSize,
        alarmStatus: '',
        alarmLevel: '',
        alarmType: '',
        siteName: '',
        meterName: '',
        deviceType: '',
        deviceCode: '',
        beginDate: '',
        endDate: '',
        realtimeOnly: '1'
      }
      this.refreshAll()
    },
    async exportExcel() {
      this.exporting = true
      try {
        const params = { ...this.buildQuery() }
        delete params.pageNum
        delete params.pageSize
        const res = await gasApi.alarm.export(params)
        const rows = res.data || []
        if (!rows.length) {
          this.$message.warning('暂无数据可导出')
          return
        }
        const headers = ['告警时间', '站点名称', '表具名称', '设备类型', '设备编号', '报警类型', '告警级别', '超时(秒)', '报警值', '设置值', '当前值', '操作人', '状态', '告警标题', '告警内容']
        const lines = [headers.join(',')]
        const esc = v => {
          const s = v == null || v === '' ? '' : String(v)
          return `"${s.replace(/"/g, '""')}"`
        }
        rows.forEach(r => {
          lines.push([
            esc(r.alarmTime),
            esc(r.siteName),
            esc(r.meterName || r.terminalCode),
            esc(r.deviceType),
            esc(r.deviceCode || r.terminalCode),
            esc(this.alarmTypeLabel(r.alarmType)),
            esc(this.levelLabel(r.alarmLevel)),
            esc(r.timeoutSeconds),
            esc(r.alarmValue),
            esc(r.setValue),
            esc(r.currentValue),
            esc(r.operatorName),
            esc(this.statusLabel(r.alarmStatus)),
            esc(r.title),
            esc(r.content)
          ].join(','))
        })
        const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `实时告警_${new Date().toISOString().slice(0, 10)}.csv`
        link.click()
        URL.revokeObjectURL(link.href)
        this.$message.success(`导出成功，共 ${rows.length} 条`)
      } finally {
        this.exporting = false
      }
    },
    onTimeRangeChange(val) {
      if (val && val.length === 2) {
        this.query.beginDate = val[0]
        this.query.endDate = val[1]
      } else {
        this.query.beginDate = ''
        this.query.endDate = ''
      }
    },
    switchLevelTab(val) {
      this.activeLevelTab = val
      this.query.alarmLevel = val
      this.query.pageNum = 1
      this.loadData()
    },
    rowNo(index) {
      return String((this.query.pageNum - 1) * this.query.pageSize + index + 1).padStart(3, '0')
    },
    normalizeStatus(s) {
      return s === 'active' ? 'pending' : (s || 'pending')
    },
    statusLabel(s) {
      return resolveStatus('alarmStatus', this.normalizeStatus(s)).label
    },
    levelLabel(v) {
      return resolveStatus('alarmLevel', v).label
    },
    alarmTypeLabel(type) {
      if (!type) return '—'
      return (TYPE_MAP[type] && TYPE_MAP[type].label) || type
    },
    typeTagClass(type) {
      return (TYPE_MAP[type] && TYPE_MAP[type].tag) || 'type-default'
    },
    formatTimeout(v) {
      if (v === null || v === undefined || v === '') return '—'
      return String(v)
    },
    valueClass(row) {
      const lv = row.alarmLevel || 'warning'
      if (lv === 'critical') return 'val-critical'
      if (lv === 'warning') return 'val-warning'
      if (lv === 'info') return 'val-info'
      return 'val-normal'
    },
    currentValueClass(row) {
      if (row.alarmStatus === 'resolved') return 'val-ok'
      return this.valueClass(row)
    },
    canProcess(row) {
      const s = this.normalizeStatus(row.alarmStatus)
      return s === 'pending'
    },
    canFinish(row) {
      return this.normalizeStatus(row.alarmStatus) === 'processing'
    },
    canShield(row) {
      const s = this.normalizeStatus(row.alarmStatus)
      return s === 'pending' || s === 'processing'
    },
    operatorParams() {
      const name = this.$store && this.$store.state && this.$store.state.name
      return name ? { operatorName: name } : {}
    },
    async processAlarm(row) {
      await this.$confirm('确认开始处理该告警？', '提示', { type: 'warning' })
      await gasApi.alarm.process(row.alarmId, this.operatorParams())
      this.$message.success('已进入处理中')
      this.refreshAll()
    },
    async finishAlarm(row) {
      await this.$confirm('确认将该告警标记为已处理？', '提示', { type: 'warning' })
      await gasApi.alarm.resolve(row.alarmId, this.operatorParams())
      this.$message.success('处理完成')
      this.refreshAll()
    },
    async shieldAlarm(row) {
      await this.$confirm('确认屏蔽该告警？屏蔽后不再在实时列表展示。', '提示', { type: 'warning' })
      await gasApi.alarm.shield(row.alarmId, this.operatorParams())
      this.$message.success('已屏蔽')
      this.refreshAll()
    },
    openDetail(row) {
      this.detail = { ...row }
      this.detailVisible = true
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/styles/report-flow.scss';

.stat-sub {
  font-size: 13px;
  font-weight: 400;
  color: #86909c;
  margin-left: 4px;
}

.table-toolbar {
  margin-bottom: 12px;
}
.level-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: center;
}
.level-tab {
  font-size: 14px;
  color: #4e5969;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding-bottom: 4px;
  border-bottom: 2px solid transparent;
  user-select: none;
}
.level-tab.active {
  color: #1677ff;
  font-weight: 600;
  border-bottom-color: #1677ff;
}
.level-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}
.dot-critical { background: #f53f3f; }
.dot-warning { background: #ff7d00; }
.dot-normal { background: #fadb14; }
.dot-info { background: #1677ff; }

.type-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
}
.type-danger { background: #ffece8; color: #f53f3f; }
.type-warn { background: #fff7e8; color: #ff7d00; }
.type-info { background: #e8f3ff; color: #1677ff; }
.type-default { background: #f2f3f5; color: #4e5969; }

.timeout-warn { color: #ff7d00; font-weight: 600; }
.val-critical { color: #f53f3f; font-weight: 600; }
.val-warning { color: #ff7d00; font-weight: 600; }
.val-normal { color: #fadb14; font-weight: 600; }
.val-info { color: #1677ff; font-weight: 600; }
.val-ok { color: #00b42a; font-weight: 600; }

.status-text { font-size: 13px; font-weight: 500; }
.st-pending { color: #f53f3f; }
.st-processing { color: #1677ff; }
.st-resolved { color: #00b42a; }
.st-shielded { color: #86909c; }

.op-process { color: #1677ff; }
.op-finish { color: #00b42a; }
.op-shield { color: #86909c; }
</style>
